import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Avatar,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { MainLayout } from '@/components/layout';
import { LoadingSpinner } from '@/components/common';
import { usersApi, type UserInfo } from '@/api';

export function EditUserPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [user, setUser] = useState<UserInfo | null>(null);
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      if (!id) return;

      try {
        const response = await usersApi.getUser(parseInt(id, 10));
        if (response.success && response.data) {
          setUser(response.data);
          setName(response.data.name || '');
          setAvatar(response.data.avatar || '');
        }
      } catch (err) {
        enqueueSnackbar(t('error.notFound'), { variant: 'error' });
        navigate('/admin/users');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, navigate, enqueueSnackbar, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setError(null);
    setSaving(true);

    try {
      await usersApi.updateUser(parseInt(id, 10), { name, avatar: avatar || undefined });
      enqueueSnackbar(t('admin.updateSuccess'), { variant: 'success' });
      navigate('/admin/users');
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.serverError');
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <LoadingSpinner />
      </MainLayout>
    );
  }

  if (!user) {
    return (
      <MainLayout>
        <Alert severity="error">{t('error.notFound')}</Alert>
      </MainLayout>
    );
  }

  return (
    <MainLayout maxWidth="sm">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('admin.editUser')}
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar src={avatar} sx={{ width: 64, height: 64 }}>
            {name?.[0] || user.email[0]}
          </Avatar>
          <Box>
            <Typography variant="subtitle1">{user.email}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t(`roles.${user.role.toLowerCase()}`)}
            </Typography>
          </Box>
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            margin="normal"
            fullWidth
            id="name"
            label={t('user.name')}
            name="name"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
          />

          <TextField
            margin="normal"
            fullWidth
            id="avatar"
            label={t('user.avatar')}
            name="avatar"
            placeholder="https://..."
            value={avatar}
            onChange={(e) => setAvatar(e.target.value)}
            disabled={saving}
            helperText="URL to avatar image"
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/admin/users')}
              disabled={saving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
            >
              {saving ? t('common.loading') : t('common.save')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </MainLayout>
  );
}
